/**
 * FWTV Phase 5 admin — Prayer request inbox.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Heart, Trash2 } from 'lucide-react';
import api from '@/lib/api';

const STATUS_OPTS = [
  { value: 'new', label: 'New' }, { value: 'praying', label: 'Praying' },
  { value: 'prayed', label: 'Prayed for' }, { value: 'closed', label: 'Closed' },
  { value: 'spam', label: 'Spam' },
];

const getErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join(' · ');
  return d || e?.message || 'Error';
};

export const PrayerInbox = () => {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['fwtv-prayer', statusFilter],
    queryFn: async () => (await api.get(`/fwtv/admin/prayer-requests${statusFilter ? `?status=${statusFilter}` : ''}`)).data,
    refetchInterval: 20000,
  });
  const rows = data?.requests || [];
  const counts = data?.counts || {};

  const update = async (id, patch) => {
    try {
      await api.put(`/fwtv/admin/prayer-requests/${id}`, patch);
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['fwtv-prayer'] });
    } catch (e) { toast.error(getErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this prayer request?')) return;
    try {
      await api.delete(`/fwtv/admin/prayer-requests/${id}`);
      qc.invalidateQueries({ queryKey: ['fwtv-prayer'] });
    } catch (e) { toast.error(getErr(e)); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Heart className="h-4 w-4" /> Prayer inbox</CardTitle>
          <CardDescription className="text-xs">
            Public form marker: <code>{'<div data-fwtv="prayer-form"></div>'}</code>. Wall marker (shows admin-approved, first-name only): <code>{'<div data-fwtv="prayer-wall" data-limit="20"></div>'}</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <button className={`px-2 py-1 rounded border ${statusFilter === '' ? 'bg-slate-100' : ''}`} onClick={() => setStatusFilter('')} data-testid="fwtv-prayer-filter-all">All ({Object.values(counts).reduce((a, b) => a + b, 0)})</button>
            {STATUS_OPTS.map((o) => (
              <button key={o.value} className={`px-2 py-1 rounded border ${statusFilter === o.value ? 'bg-slate-100' : ''}`} onClick={() => setStatusFilter(o.value)} data-testid={`fwtv-prayer-filter-${o.value}`}>
                {o.label} ({counts[o.value] || 0})
              </button>
            ))}
          </div>

          {isLoading ? <Loader2 className="animate-spin mx-auto" /> : rows.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">No requests in this bucket.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.id} className="border rounded p-3 text-xs" data-testid={`fwtv-prayer-row-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-sm">{r.name}</span>
                      {r.email && <span className="text-slate-500 ml-2">{r.email}</span>}
                      {r.category && <Badge variant="outline" className="ml-2 text-[10px]">{r.category}</Badge>}
                      {r.is_public && <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">wall</Badge>}
                      <Badge variant="outline" className="ml-2 text-[10px]">{r.status}</Badge>
                    </div>
                    <div className="flex gap-1">
                      {STATUS_OPTS.filter((o) => o.value !== r.status).map((o) => (
                        <Button key={o.value} size="sm" variant="outline" onClick={() => update(r.id, { status: o.value, is_public: r.is_public })} data-testid={`fwtv-prayer-status-${o.value}-${r.id}`}>
                          {o.label}
                        </Button>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => update(r.id, { status: r.status, is_public: !r.is_public })} data-testid={`fwtv-prayer-toggle-public-${r.id}`}>
                        {r.is_public ? 'Hide' : 'Show'} on wall
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(r.id)} data-testid={`fwtv-prayer-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <p className="mt-1 text-slate-700">{r.message}</p>
                  {r.admin_notes && <p className="text-slate-500 mt-1 italic">Notes: {r.admin_notes}</p>}
                  <button className="text-slate-400 hover:text-slate-600 mt-1" onClick={() => { setExpanded(expanded === r.id ? null : r.id); setNotes(r.admin_notes || ''); }} data-testid={`fwtv-prayer-notes-toggle-${r.id}`}>
                    {expanded === r.id ? 'Cancel notes' : 'Add / edit notes'}
                  </button>
                  {expanded === r.id && (
                    <div className="mt-1">
                      <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`fwtv-prayer-notes-${r.id}`} />
                      <Button size="sm" className="mt-1" onClick={() => { update(r.id, { status: r.status, admin_notes: notes, is_public: r.is_public }); setExpanded(null); }} data-testid={`fwtv-prayer-notes-save-${r.id}`}>Save notes</Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrayerInbox;
